<template>
  <v-container>
    <v-row class="mb-6">
      <v-col cols="12">
        <h1>My Offers</h1>
        <p class="text-body1 text-medium-emphasis">Review and respond to booking offers</p>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" closable @click:close="error = null">
      {{ error }}
    </v-alert>

    <!-- Tabs for offer states -->
    <v-tabs v-model="tab" class="mb-4">
      <v-tab value="pending">
        Pending
        <v-badge v-if="pendingGroups.length > 0" color="primary" :content="pendingGroups.length"></v-badge>
      </v-tab>
      <v-tab value="accepted">
        Accepted
        <v-badge v-if="acceptedOffers.length > 0" color="success" :content="acceptedOffers.length"></v-badge>
      </v-tab>
      <v-tab value="past">Past</v-tab>
      <v-tab value="cancelled">Cancelled</v-tab>
      <v-tab value="declined">Declined</v-tab>
      <v-tab v-if="orgStore.organizations.length" value="open">Open Positions</v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <!-- Open positions: org members only (not non-member crew) — browse
           every open position on an upcoming gig, grouped by organization,
           and self-request it. -->
      <v-window-item value="open">
        <v-row v-if="loadingOpen">
          <v-col cols="12"><v-skeleton-loader type="table-tbody" /></v-col>
        </v-row>
        <v-row v-else-if="openPositions.length === 0">
          <v-col cols="12">
            <v-card>
              <v-card-text class="text-center py-12">
                <p class="text-body2 text-medium-emphasis">No open positions right now</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-else>
          <template v-for="group in openPositionsByOrg" :key="group.org.id">
          <v-col cols="12" class="pb-0">
            <h2 class="text-subtitle-1">{{ group.org.name }}</h2>
          </v-col>
          <v-col v-for="item in group.items" :key="item.position.id" cols="12">
            <v-card>
              <v-card-item>
                <template #title>{{ item.gig.name }}</template>
                <template #subtitle>{{ item.position.role }}</template>
              </v-card-item>
              <v-card-text>
                <p class="text-body2 text-medium-emphasis">{{ item.gig.clientName }}</p>
                <template v-if="item.calls.length">
                  <p v-for="c in item.calls" :key="c.id" class="text-body2">
                    {{ c.type }} — {{ formatDateTime(c.startTime, venueZone(item.orgId, c.venueId)) }} → {{ formatDateTime(c.endTime, venueZone(item.orgId, c.venueId), true) }}
                  </p>
                  <p v-if="venueName(item.orgId, item.calls[0].venueId)" class="text-body2">
                    <strong>Venue:</strong> {{ venueName(item.orgId, item.calls[0].venueId) }}
                  </p>
                </template>
                <template v-else>
                  <p class="text-body2">
                    <strong>Day:</strong> {{ item.position.scopeDate ? formatDateTime(item.position.scopeDate, venueZone(item.orgId, item.position.venueId)) : 'TBD' }}
                  </p>
                  <p v-if="venueName(item.orgId, item.position.venueId)" class="text-body2">
                    <strong>Venue:</strong> {{ venueName(item.orgId, item.position.venueId) }}
                  </p>
                </template>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  color="primary"
                  :loading="requestingPositionId === item.position.id"
                  :disabled="requestedPositionIds.has(item.position.id)"
                  @click="handleRequestToFill(item)"
                >
                  {{ requestedPositionIds.has(item.position.id) ? 'Requested' : 'Request to Fill' }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
          </template>
        </v-row>
      </v-window-item>

      <!-- Pending Offers -->
      <v-window-item value="pending">
        <v-row v-if="pendingGroups.length === 0">
          <v-col cols="12">
            <v-card>
              <v-card-text class="text-center py-12">
                <v-icon size="48" class="text-medium-emphasis mb-4">mdi-inbox-outline</v-icon>
                <p class="text-h6">No pending offers</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col v-for="group in pendingGroups" :key="group.key" cols="12">
            <v-card :data-testid="group.offers.length > 1 ? 'shared-offer-card' : 'offer-card'">
              <v-card-item>
                <template #title>
                  <template v-if="group.offers.length > 1">One day, {{ group.offers.length }} gigs</template>
                  <template v-else>{{ group.primary.gigName }}</template>
                </template>
                <template #subtitle>
                  <template v-if="group.offers.length > 1">{{ group.offers.map(o => o.gigName).join(' + ') }}</template>
                  <template v-else>{{ group.primary.role }}</template>
                </template>
              </v-card-item>

              <v-card-text>
                <v-row dense class="mb-4">
                  <v-col cols="12" sm="6">
                    <p class="text-caption text-medium-emphasis">Rate</p>
                    <p class="text-body2 font-weight-medium">{{ rateLabel(group.primary) }}</p>
                    <p v-if="group.offers.length > 1" class="text-caption text-medium-emphasis">
                      One rate for the whole day. Overtime counts your combined hours.
                    </p>
                  </v-col>
                </v-row>

                <div v-for="offer in group.offers" :key="offer.id" class="mb-3">
                  <p v-if="group.offers.length > 1" class="text-body2 font-weight-medium mb-1">
                    {{ offer.gigName }} — {{ offer.role }}
                  </p>
                  <template v-if="offer.linkedCalls?.length">
                    <p v-if="group.offers.length === 1" class="text-caption text-medium-emphasis mb-1">
                      {{ offer.linkedCalls.length > 1 ? 'Covers' : 'When' }}
                    </p>
                    <p v-for="c in offer.linkedCalls" :key="c.callId" class="text-body2">
                      {{ c.type }} — {{ formatDateTime(c.startTime, c.timeZone) }} → {{ formatDateTime(c.endTime, c.timeZone, true) }}
                    </p>
                    <p v-if="offer.linkedCalls[0].venueName" class="text-body2 mt-1">
                      <strong>Venue:</strong> {{ offer.linkedCalls[0].venueName }}
                    </p>
                  </template>
                  <template v-else>
                    <p class="text-body2">
                      <strong>Day:</strong> {{ offer.scopeDate ? formatDateTime(offer.scopeDate, offer.venueTimeZone) : 'TBD' }}
                    </p>
                    <p v-if="offer.venueName" class="text-body2">
                      <strong>Venue:</strong> {{ offer.venueName }}
                    </p>
                  </template>
                </div>

                <p v-if="termsText(group.primary)" class="text-caption text-medium-emphasis mt-3 mb-0">
                  {{ termsText(group.primary) }}
                </p>
                <p class="text-caption text-medium-emphasis mt-1 mb-0">
                  This offer is for the full day/venue/role above at the agreed rate — any call times shown
                  are what's currently planned, not a hard cap, and may shift as the gig firms up.
                </p>
              </v-card-text>

              <v-card-actions>
                <v-btn color="success" :loading="respondingOfferId === group.key" @click="handleRespondGroup(group.offers, true, group.key)">
                  Accept<template v-if="group.offers.length > 1"> all</template>
                </v-btn>
                <v-btn color="error" variant="outlined" :loading="respondingOfferId === group.key" @click="handleRespondGroup(group.offers, false, group.key)">
                  Decline<template v-if="group.offers.length > 1"> all</template>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Accepted Offers -->
      <v-window-item value="accepted">
        <v-row v-if="acceptedOffers.length === 0">
          <v-col cols="12">
            <v-card>
              <v-card-text class="text-center py-12">
                <p class="text-body2 text-medium-emphasis">No accepted offers</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col v-for="offer in acceptedOffers" :key="offer.id" cols="12">
            <v-card>
              <v-card-item>
                <template #title>{{ offer.gigName }}</template>
                <template #subtitle>{{ offer.role }}</template>
              </v-card-item>

              <v-card-text>
                <v-row dense class="mb-4">
                  <v-col cols="12" sm="6">
                    <p class="text-caption text-medium-emphasis">Rate</p>
                    <p class="text-body2">{{ rateLabel(offer) }}</p>
                  </v-col>
                </v-row>

                <template v-if="offer.linkedCalls?.length">
                  <p v-for="c in offer.linkedCalls" :key="c.callId" class="text-body2">
                    {{ c.type }} — {{ formatDateTime(c.startTime, c.timeZone) }} → {{ formatDateTime(c.endTime, c.timeZone, true) }}
                  </p>
                  <p v-if="offer.linkedCalls[0].venueName" class="text-body2">
                    <strong>Venue:</strong> {{ offer.linkedCalls[0].venueName }}
                  </p>
                </template>
                <template v-else>
                  <p class="text-body2">
                    <strong>Day:</strong> {{ offer.scopeDate ? formatDateTime(offer.scopeDate, offer.venueTimeZone) : 'TBD' }}
                  </p>
                  <p v-if="offer.venueName" class="text-body2">
                    <strong>Venue:</strong> {{ offer.venueName }}
                  </p>
                </template>

                <v-chip color="success" size="small" class="mt-2">Accepted</v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Past — accepted work that's already happened. Separate from
           Accepted (which is upcoming-only) so that tab stays "what's next"
           instead of accumulating every gig ever worked. -->
      <v-window-item value="past">
        <v-row v-if="pastOffers.length === 0">
          <v-col cols="12">
            <v-card>
              <v-card-text class="text-center py-12">
                <p class="text-body2 text-medium-emphasis">No past gigs yet</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col v-for="offer in pastOffers" :key="offer.id" cols="12">
            <v-card>
              <v-card-item>
                <template #title>{{ offer.gigName }}</template>
                <template #subtitle>{{ offer.role }}</template>
              </v-card-item>

              <v-card-text>
                <v-row dense class="mb-4">
                  <v-col cols="12" sm="6">
                    <p class="text-caption text-medium-emphasis">Rate</p>
                    <p class="text-body2">{{ rateLabel(offer) }}</p>
                  </v-col>
                </v-row>

                <template v-if="offer.linkedCalls?.length">
                  <p v-for="c in offer.linkedCalls" :key="c.callId" class="text-body2">
                    {{ c.type }} — {{ formatDateTime(c.startTime, c.timeZone) }} → {{ formatDateTime(c.endTime, c.timeZone, true) }}
                  </p>
                  <p v-if="offer.linkedCalls[0].venueName" class="text-body2">
                    <strong>Venue:</strong> {{ offer.linkedCalls[0].venueName }}
                  </p>
                </template>
                <template v-else>
                  <p class="text-body2">
                    <strong>Day:</strong> {{ offer.scopeDate ? formatDateTime(offer.scopeDate, offer.venueTimeZone) : 'TBD' }}
                  </p>
                  <p v-if="offer.venueName" class="text-body2">
                    <strong>Venue:</strong> {{ offer.venueName }}
                  </p>
                </template>

                <v-chip color="success" size="small" class="mt-2">Worked</v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Cancelled — a commitment the coordinator pulled after it was
           offered/accepted. Distinct from Declined (which is the crew
           member's own choice). Shows any fee, since it's their money. -->
      <v-window-item value="cancelled">
        <v-row v-if="cancelledOffers.length === 0">
          <v-col cols="12">
            <v-card>
              <v-card-text class="text-center py-12">
                <p class="text-body2 text-medium-emphasis">No cancelled offers</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col v-for="offer in cancelledOffers" :key="offer.id" cols="12">
            <v-card>
              <v-card-item>
                <template #title>{{ offer.gigName }}</template>
                <template #subtitle>{{ offer.role }}</template>
              </v-card-item>

              <v-card-text>
                <p class="text-body2 mb-2">
                  {{ offer.linkedCalls?.[0] ? formatDateTime(offer.linkedCalls[0].startTime, offer.linkedCalls[0].timeZone) : (offer.scopeDate ? formatDateTime(offer.scopeDate, offer.venueTimeZone) : '—') }}
                </p>
                <p v-if="offer.cancellationReason" class="text-body2 text-medium-emphasis mb-2">{{ offer.cancellationReason }}</p>
                <p v-if="offer.cancellationFee" class="text-body2 font-weight-medium mb-2">
                  Cancellation Fee: ${{ offer.cancellationFee.amount.toLocaleString() }} ({{ offer.cancellationFeeStatus?.toLowerCase() }})
                </p>
                <v-chip color="grey-darken-2" size="small">Cancelled</v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Declined Offers -->
      <v-window-item value="declined">
        <v-row v-if="declinedOffers.length === 0">
          <v-col cols="12">
            <v-card>
              <v-card-text class="text-center py-12">
                <p class="text-body2 text-medium-emphasis">No declined offers</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col v-for="offer in declinedOffers" :key="offer.id" cols="12">
            <v-card>
              <v-card-item>
                <template #title>{{ offer.gigName }}</template>
                <template #subtitle>{{ offer.role }}</template>
              </v-card-item>

              <v-card-text>
                <p class="text-body2 mb-4">
                  {{ offer.linkedCalls?.[0] ? formatDateTime(offer.linkedCalls[0].startTime, offer.linkedCalls[0].timeZone) : (offer.scopeDate ? formatDateTime(offer.scopeDate, offer.venueTimeZone) : '—') }}
                </p>
                <v-chip color="error" size="small">Declined</v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>
    </v-window>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import type { Booking } from '@gigdriver/domain';
import { isPastBooking } from '@gigdriver/domain';
import type { Venue, Organization } from '@gigdriver/domain';
import { getPersonOffers, respondToOffer, respondToSharedOffer, getOpenPositions, requestToFill, getOrgVenues, type OpenPositionListing } from '@gigdriver/client';
import { useAuthStore } from '../stores/auth';
import { useOrganizationStore } from '../stores/organization';

const authStore = useAuthStore();
const orgStore = useOrganizationStore();

const tab = ref('pending');
const offers = ref<Booking[]>([]);
const error = ref<string | null>(null);
const respondingOfferId = ref<string | null>(null);

// Open positions across EVERY organization the user belongs to (a position's
// listing carries its org so a request goes to the right one).
type OrgOpenPosition = OpenPositionListing & { orgId: string };
const openPositions = ref<OrgOpenPosition[]>([]);
const venuesByOrg = ref<Record<string, Venue[]>>({});
const loadingOpen = ref(false);
const requestingPositionId = ref<string | null>(null);
// Derived from the person's own bookings (getPersonOffers returns every
// state, DRAFT included) rather than tracked in memory — a purely local Set
// forgot every self-request as soon as the user navigated away and back,
// letting them request the same position over and over and fill the
// staffing budget with duplicate draft bookings.
const requestedPositionIds = computed(() => new Set(offers.value.filter(o => o.state === 'DRAFT').map(o => o.positionId)));

function venueName(orgId: string, venueId?: string): string | undefined {
  return venueId ? venuesByOrg.value[orgId]?.find(v => v.id === venueId)?.name : undefined;
}
function venueZone(orgId: string, venueId?: string): string | undefined {
  return venueId ? venuesByOrg.value[orgId]?.find(v => v.id === venueId)?.timeZone : undefined;
}

function openPositionSortTime(item: OpenPositionListing): number {
  const d = item.calls[0]?.startTime ?? item.position.scopeDate;
  return d ? new Date(d).getTime() : Infinity;
}
// One group per organization (that has any open position), soonest first.
const openPositionsByOrg = computed<{ org: Organization; items: OrgOpenPosition[] }[]>(() =>
  orgStore.organizations
    .map(org => ({
      org,
      items: sortByNearestToToday(openPositions.value.filter(p => p.orgId === org.id), openPositionSortTime),
    }))
    .filter(g => g.items.length > 0),
);

async function loadOpenPositions() {
  const orgs = orgStore.organizations;
  if (orgs.length === 0) {
    openPositions.value = [];
    return;
  }
  try {
    loadingOpen.value = true;
    const perOrg = await Promise.all(
      orgs.map(async org => {
        const [listings, venues] = await Promise.all([getOpenPositions(org.id), getOrgVenues(org.id)]);
        return { org, listings, venues };
      }),
    );
    venuesByOrg.value = Object.fromEntries(perOrg.map(o => [o.org.id, o.venues]));
    openPositions.value = perOrg.flatMap(o => o.listings.map(l => ({ ...l, orgId: o.org.id })));
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load open positions';
  } finally {
    loadingOpen.value = false;
  }
}

watch(() => orgStore.organizations.map(o => o.id).join(','), loadOpenPositions, { immediate: true });

async function handleRequestToFill(item: OrgOpenPosition) {
  if (!authStore.currentUser) return;
  try {
    requestingPositionId.value = item.position.id;
    error.value = null;
    await requestToFill(item.orgId, item.gig.id, authStore.currentUser.uid, item.position);
    await loadOffers();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to request this position';
  } finally {
    requestingPositionId.value = null;
  }
}

// The date an offer/booking card should be sorted by — its first linked
// call's start, falling back to its call-less scopeDate. Undated cards sink
// to the end rather than clustering at the (misleading) top.
function offerSortTime(offer: Booking): number {
  const d = offer.linkedCalls?.[0]?.startTime ?? offer.scopeDate;
  return d ? new Date(d).getTime() : Infinity;
}
// Nearest to today first, whichever direction — matches the Gigs list, and
// means each of these already-time-scoped tabs reads correctly without
// special-casing: soonest-first for the upcoming tabs (Pending/Accepted,
// which only ever contain future items), most-recent-first for the history
// tabs (Past/Cancelled/Declined, which only ever contain past ones).
function sortByNearestToToday<T>(items: T[], dateOf: (item: T) => number): T[] {
  const now = Date.now();
  return [...items].sort((a, b) => Math.abs(dateOf(a) - now) - Math.abs(dateOf(b) - now));
}

// A Booking is already the whole offer now (one person, one position, its
// own callIds) — no more grouping step needed.
const pendingOffers = computed(() => sortByNearestToToday(offers.value.filter(o => o.state === 'OFFERED'), offerSortTime));
// Bookings on one shared day (Planning/SharedDay.md) arrive as ONE card with one
// Accept and one Decline; the primary booking supplies the rate and terms.
// Anything else is a card of one.
const pendingGroups = computed(() => {
  const groups: Array<{ key: string; primary: Booking; offers: Booking[] }> = [];
  const bySharedDay = new Map<string, (typeof groups)[number]>();
  for (const offer of pendingOffers.value) {
    const sharedId = offer.sharedDayId;
    const existing = sharedId ? bySharedDay.get(sharedId) : undefined;
    if (existing) {
      existing.offers.push(offer);
      if (offer.isSharedPrimary) existing.primary = offer;
      continue;
    }
    const group = { key: sharedId ?? offer.id, primary: offer, offers: [offer] };
    groups.push(group);
    if (sharedId) bySharedDay.set(sharedId, group);
  }
  return groups;
});
// getPersonOffers is a cross-org query (this page can show offers from more
// than one org at once), but isPastBooking needs ONE time zone — falls back
// to the current org's (or the browser's own) rather than trying to resolve
// a per-offer org timezone just for this split. Only affects the call-less/
// scopeDate-only fallback path; any offer with real linked calls compares
// absolute instants and isn't affected by which zone is used here.
const timeZone = computed(() => orgStore.currentOrg?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
const acceptedOffers = computed(() =>
  sortByNearestToToday(
    offers.value.filter(o => o.state === 'ACCEPTED' && !isPastBooking(o, new Date(), o.venueTimeZone ?? timeZone.value)),
    offerSortTime,
  ),
);
const pastOffers = computed(() =>
  sortByNearestToToday(
    offers.value.filter(o => (o.state === 'ACCEPTED' || o.state === 'BOOKED') && isPastBooking(o, new Date(), o.venueTimeZone ?? timeZone.value)),
    offerSortTime,
  ),
);
const cancelledOffers = computed(() => sortByNearestToToday(offers.value.filter(o => o.state === 'CANCELLED'), offerSortTime));
const declinedOffers = computed(() => sortByNearestToToday(offers.value.filter(o => o.state === 'DECLINED'), offerSortTime));

async function loadOffers() {
  if (!authStore.currentUser) return;
  try {
    error.value = null;
    offers.value = await getPersonOffers(authStore.currentUser.uid);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load offers';
  }
}

onMounted(loadOffers);
// currentUser resolves asynchronously (onAuthStateChanged) — for a
// brand-new account, this component can mount before it's populated, and
// the onMounted call above becomes a no-op with nothing to re-trigger it.
// Reload once it (or the account behind it) actually becomes available.
watch(() => authStore.currentUser?.uid, uid => {
  if (uid) loadOffers();
});

// `zone` = the venue's IANA zone (when known) — shown in that zone with its
// abbreviation on request, otherwise in the viewer's own zone.
function formatDateTime(date: any, zone?: string, withZoneName = false): string {
  if (!date) return '—';
  return new Date(date).toLocaleString('en-US', {
    ...(zone ? { timeZone: zone } : {}),
    ...(zone && withZoneName ? { timeZoneName: 'short' as const } : {}),
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function rateLabel(offer: Booking): string {
  const amount = offer.rate?.amount;
  if (amount == null) return '—';
  const suffix = offer.rateType === 'hourly' ? '/hr' : offer.rateType === 'day' ? '/day' : '';
  return `$${amount}${suffix}`;
}

function hourWord(n: number): string {
  return `${n} hour${n === 1 ? '' : 's'}`;
}

// Mirrors GigDetail.vue's previewTermsText / functions/src/email.ts's
// offerCreatedEmail termBullets — same branching (useStandardTimekeeping,
// then rate type), same defaults. Reads offer.offerTerms rather than a rate
// card directly: this is the offered person's own view, which (unlike a
// coordinator's) has no read access to the org's rate cards, so the terms
// are denormalized onto the booking itself at offer-creation time.
function termsText(offer: Booking): string | null {
  const terms = offer.offerTerms;
  if (!terms) return null;
  const useDayRate = offer.rateType !== 'hourly';
  if (terms.useStandardTimekeeping) {
    const otStartHrs = terms.otStartHrs ?? (useDayRate ? 10 : 8);
    const ot2StartHrs = terms.otSecondStartHrs ?? 12;
    const parts = [`Overtime: 1.5x after ${hourWord(otStartHrs)}, 2x after ${hourWord(ot2StartHrs)}`];
    const rest = terms.restPeriodHrs ?? 10;
    if (useDayRate) {
      // The estimate assumes the on-site coordinator provides breaks and meals;
      // rest and meal rules are applied to actual time worked.
      parts.push(`Day rate covers up to ${hourWord(otStartHrs)} per day — your estimate assumes the on-site coordinator provides your breaks and meals`);
      parts.push(`${hourWord(rest)} rest between days, applied to actual time worked: if it isn't given, the following day bills at 1.5x the day rate`);
      parts.push('Meal break required before hour 5, applied to actual time worked: the day otherwise concludes at hour 5 (can continue as OT after a delayed break)');
    } else {
      const firstCallMinHrs = terms.firstCallMinHrs ?? 4;
      const subsequentCallMinHrs = terms.subsequentCallMinHrs ?? 2;
      parts.push(`${hourWord(firstCallMinHrs)} minimum for your first call of the day, ${hourWord(subsequentCallMinHrs)} for each additional call`);
      parts.push(`${hourWord(rest)} rest between calls to reset the overtime clock`);
      parts.push('Meal break required after 5 hours');
    }
    parts.push('Recognized holidays bill as overtime for the full call/day');
    return parts.join(' · ');
  }
  const parts = [`Overtime after ${hourWord(terms.otStartHrs)}`];
  if (terms.restPeriodHrs) parts.push(`${hourWord(terms.restPeriodHrs)} rest period between daily calls`);
  if (terms.minHourlyBill) parts.push(`${hourWord(terms.minHourlyBill)} minimum per call`);
  return parts.join(' · ');
}

async function handleRespondGroup(group: Booking[], accept: boolean, key: string) {
  try {
    respondingOfferId.value = key;
    error.value = null;
    if (group.length > 1) await respondToSharedOffer(group, accept);
    else await respondToOffer(group[0].orgId, group[0].gigId, group[0].positionId, group[0].id, accept);
    await loadOffers();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to respond to offer';
  } finally {
    respondingOfferId.value = null;
  }
}

async function handleRespond(offerId: string, accept: boolean) {
  const offer = offers.value.find(o => o.id === offerId);
  if (!offer) return;
  try {
    respondingOfferId.value = offerId;
    error.value = null;
    await respondToOffer(offer.orgId, offer.gigId, offer.positionId, offer.id, accept);
    await loadOffers();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to respond to offer';
  } finally {
    respondingOfferId.value = null;
  }
}
</script>
